import { ref } from "vue";
import { RouteRecordRaw, createRouter, createWebHistory, NavigationGuardNext, RouteLocationRaw } from "vue-router";
import { FORM_MODE, FormMode, ROUTE_NAMES, ROUTE_PATHS } from "./types";
import { scrollToTop } from "@/utils";
import { FormRouteName } from "./utils";

import { useMainStore } from "@/stores/main";
import { useProposalStore } from "@/stores/risk/risk";
import { useMainPersistentStore } from "@/stores/mainPersistent";
import DeclinedPage from "@/views/Errors/Declined.vue";
import YourCover from "@/views/YourCover.vue";
import AboutYou from "@/views/AboutYou.vue";
import YourProperty from "@/views/YourProperty.vue";
import YourQuotes from "@/views/YourQuotes.vue";
import PaymentPage from "@/views/PaymentPage.vue";
import DirectDebitPage from "@/views/DirectDebit/DirectDebitPage.vue";
import ThankYou from "@/views/ThankYou.vue";
import ErrorPage from "@/views/Errors/Error.vue";
import SessionExpired from "@/views/Errors/SessionExpired.vue";
import PaymentFailed from "@/views/Errors/PaymentFailed.vue";
import MarketingPage from "@/views/MarketingPage.vue";
import PageNotFound from "@/views/Errors/PageNotFound.vue";
import QuoteConfirm from "@/views/QuoteConfirm.vue";
import RetrieveQuoteForm from "@/components/RetrieveQuoteForm.vue";
import { GA4Helper, PageInfoParam } from "@/utils/ga4-dataLayers";
import { APP_STEP } from "@/stores/types";
import MarketingPreferences from "@/views/MarketingPreferences.vue";
import MarketingPreferencesSuccess from "@/views/MarketingPreferencesSuccess.vue";

const resolveNavigation = ref<NavigationGuardNext | null>(null);

window.addEventListener("popstate", () => {
    if (sessionStorage.getItem("forceStopNavigation") !== "true") {
        const mainStore = useMainStore();
        mainStore.stopNavigation = true;
    }
});

function getFormRoutes(formMode: FormMode): Array<RouteRecordRaw> {
    return [
        {
            path: ROUTE_PATHS.AboutYou,
            name: FormRouteName(ROUTE_NAMES.AboutYou, formMode),
            component: AboutYou,
            meta: {
                appStep: APP_STEP.ABOUT_YOU,
                ga4DataLayerName: "customerDetailsView",
                title: "Insurance Calculator – About You"
            }
        },
        {
            path: ROUTE_PATHS.YourProperty,
            name: FormRouteName(ROUTE_NAMES.YourProperty, formMode),
            component: YourProperty,
            meta: {
                appStep: APP_STEP.YOUR_PROPERTY,
                ga4DataLayerName: "propertyDetailsView",
                title: "Insurance Calculator – Your Property"
            }
        },
        {
            path: ROUTE_PATHS.YourCover,
            name: FormRouteName(ROUTE_NAMES.YourCover, formMode),
            component: YourCover,
            meta: {
                appStep: APP_STEP.YOUR_COVER,
                ga4DataLayerName: "coverageDetailsView",
                title: "Insurance Calculator – Your Cover"
            }
        }
    ];
}

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: "/",
            redirect: {
                name: FormRouteName(ROUTE_NAMES.AboutYou, FORM_MODE.CREATE)
            }
        },
        {
            path: ROUTE_PATHS.CREATE,
            name: FORM_MODE.CREATE,
            children: getFormRoutes(FORM_MODE.CREATE),
            redirect: { name: FormRouteName(ROUTE_NAMES.AboutYou, FORM_MODE.CREATE) }
        },
        {
            path: ROUTE_PATHS.EDIT,
            name: FORM_MODE.EDIT,
            children: getFormRoutes(FORM_MODE.EDIT),
            redirect: { name: FormRouteName(ROUTE_NAMES.AboutYou, FORM_MODE.EDIT) }
        },
        {
            path: ROUTE_PATHS.YourQuotes,
            name: ROUTE_NAMES.YourQuotes,
            component: YourQuotes,
            meta: {
                ga4DataLayerName: "quotePriceViewA",
                title: "Insurance Calculator – Quotes"
            }
        },
        {
            path: ROUTE_PATHS.QuoteConfirm,
            name: ROUTE_NAMES.QuoteConfirm,
            component: QuoteConfirm,
            meta: {
                ga4DataLayerName: "quotePriceViewB",
                title: "Insurance Calculator – Quote Confirmation"
            }
        },
        {
            path: ROUTE_PATHS.Marketing,
            name: ROUTE_NAMES.Marketing,
            component: MarketingPage,
            meta: {
                ga4DataLayerName: "postQuoteQuestionsView",
                title: "Insurance Calculator – Marketing Preferences"
            }
        },
        {
            path: ROUTE_PATHS.Payment,
            name: ROUTE_NAMES.Payment,
            component: PaymentPage,
            meta: {
                ga4DataLayerName: "paymentInfocreditcardfull",
                title: "Insurance Calculator – Payment"
            }
        },
        {
            path: ROUTE_PATHS.QuoteRecall,
            name: ROUTE_NAMES.QuoteRecall,
            component: RetrieveQuoteForm
        },
        {
            path: ROUTE_PATHS.MarketingPreferencesLogin,
            name: ROUTE_NAMES.MarketingPreferencesLogin,
            component: RetrieveQuoteForm
        },
        {
            path: ROUTE_PATHS.MarketingPreferences,
            name: ROUTE_NAMES.MarketingPreferences,
            component: MarketingPreferences
        },
        {
            path: ROUTE_PATHS.MarketingPreferencesSuccess,
            name: ROUTE_NAMES.MarketingPreferencesSuccess,
            component: MarketingPreferencesSuccess
        },
        {
            path: ROUTE_PATHS.DirectDebit,
            name: ROUTE_NAMES.DirectDebit,
            component: DirectDebitPage,
            meta: {
                ga4DataLayerName: "paymentInfobankdetails",
                title: "Insurance Calculator – Direct Debit"
            }
        },
        {
            path: ROUTE_PATHS.ThankYou,
            name: ROUTE_NAMES.ThankYou,
            component: ThankYou,
            meta: {
                title: "Insurance Calculator – Thank You"
            }
        },
        {
            path: ROUTE_PATHS.Error,
            name: ROUTE_NAMES.Error,
            component: ErrorPage,
            meta: {
                errorMessage: "Technical Error",
                ga4DataLayerEvent: "quoteRefer",
                title: "An Post Insurance – Error"
            }
        },
        {
            path: ROUTE_PATHS.SessionExpired,
            name: ROUTE_NAMES.SessionExpired,
            component: SessionExpired,
            meta: {
                errorMessage: "Session Expired",
                ga4DataLayerEvent: "quoteRefer",
                title: "An Post Insurance – Session Expired"
            }
        },
        {
            path: ROUTE_PATHS.Declined,
            name: ROUTE_NAMES.Declined,
            component: DeclinedPage,
            meta: {
                errorMessage: "Quote Declined",
                ga4DataLayerEvent: "quoteRefer",
                title: "An Post Insurance – Quote Declined"
            }
        },
        {
            path: ROUTE_PATHS.PaymentFailed,
            name: ROUTE_NAMES.PaymentFailed,
            component: PaymentFailed,
            meta: {
                errorMessage: "Payment Failed",
                ga4DataLayerEvent: "quoteRefer",
                title: "An Post Insurance – Payment Failed"
            }
        },
        {
            path: "/:pathMatch(.*)*", //catch all
            component: PageNotFound
        }
    ]
});

function hasQueryParams(route) {
    return !!Object.keys(route.query).length;
}

const queryParamExemptRoutes = [ROUTE_NAMES.MarketingPreferencesLogin];

router.beforeEach((to, from, next) => {
    const mainStore = useMainStore();

    scrollToTop();

    mainStore.previousRoute = from;

    mainStore.setResetQuote(
        (from.path === ROUTE_PATHS.YourQuotes && !from.path.includes("/quote/")) || from.path === ROUTE_PATHS.Marketing
    );
    if (mainStore.stopNavigation) {
        mainStore.stopNavigation = false;
        mainStore.displayBrowserNavigationModal = true;
        resolveNavigation.value = next;
        return;
    } else {
        sessionStorage.removeItem("forceStopNavigation");
    }

    if (
        !hasQueryParams(to) &&
        hasQueryParams(from) &&
        to.name &&
        !queryParamExemptRoutes.includes(to.name as ROUTE_NAMES)
    ) {
        next({ name: to.name ?? "", query: from.query });
    } else {
        next();
    }
});

// GA4  DataLayer Tracking
router.afterEach(async (to, from) => {
    document.title = (to.meta?.title as string) || "An Post Insurance";

    setTimeout(async () => {
        const pageTitle = typeof to.name === "string" ? to.name : "Unnamed Page";
        const pagePath = to.path;
        const eventName = (to.meta.ga4DataLayerName as string) || "spa_page_view";

        // return_url for payment iframe is /thank-you, if sucessful, we already manually track the purchase event in the quoteWrapUp function called from the ThankYou view.
        // This prevents /thank-you being tracked twice and stops it being tracked if payment fails.
        // routeGuard will redirect to PaymentFailed if payment fails and will track it here on route change.
        if (pagePath === ROUTE_PATHS.ThankYou) {
            return;
        }

        if (to.meta?.ga4DataLayerEvent && typeof to.meta.errorMessage === "string") {
            if (pagePath === ROUTE_PATHS.PaymentFailed) {
                const isPayMonthly = useProposalStore().proposal?.is_pay_monthly;
                const pageInfo: PageInfoParam = {
                    page: {
                        title: isPayMonthly ? ROUTE_NAMES.DirectDebit : ROUTE_NAMES.Payment,
                        url: isPayMonthly ? ROUTE_PATHS.DirectDebit : ROUTE_PATHS.Payment,
                    }
                }
                GA4Helper.trackQuoteRefer(to.meta.errorMessage, "", "", pageInfo);
            } else {
                GA4Helper.trackQuoteRefer(to.meta.errorMessage);
            }
        } else {
            await GA4Helper.trackPageView({
                eventName,
                pageTitle,
                pagePath
            });
        }
    }, 100);
});

function routeGuard() {
    if (router.currentRoute.value.matched.length === 0) return;
    try {
        const proposalStore = useProposalStore();
        const mainStorePersistent = useMainPersistentStore();
        let replaceRoute = false;

        const isEditRoute = router.currentRoute.value.matched[0].name === FORM_MODE.EDIT;
        const isCreateRoute = router.currentRoute.value.matched[0].name === FORM_MODE.CREATE;

        const isQuotePage = router.currentRoute.value.name === ROUTE_NAMES.YourQuotes;
        const isPaymentPage = router.currentRoute.value.name === ROUTE_NAMES.Payment;
        const isThankYouPage = router.currentRoute.value.name === ROUTE_NAMES.ThankYou;
        const isPreferencesPage =
            router.currentRoute.value.name === ROUTE_NAMES.MarketingPreferences ||
            router.currentRoute.value.name === ROUTE_NAMES.MarketingPreferencesSuccess;
        const hasUUID = !!proposalStore.proposal.uuid || !!router.currentRoute.value.query.uuid;

        let targetRoute = router.currentRoute.value.name;

        /**
         * Handle incorrect Create/Edit routes
         */
        if (isEditRoute || isCreateRoute) {
            if (isCreateRoute && hasUUID) {
                targetRoute = FORM_MODE.EDIT;
            } else if (isEditRoute && !hasUUID) {
                targetRoute = FORM_MODE.CREATE;
            }
            // If a form route, make sure user can't access any steps past current max step
            if (isCreateRoute) {
                const currentMaxStep = mainStorePersistent.guardStep;
                const currentRouteStep = router.currentRoute.value.meta.appStep as number;
                if (currentRouteStep > currentMaxStep) {
                    return router.replace({ name: FormRouteName(ROUTE_NAMES.AboutYou, FORM_MODE.CREATE) ?? "" });
                }
            }
        }

        //If attempting to access the quote or payment page without a quote, redirect to the first form page
        if (isQuotePage || isPaymentPage) {
            if (!proposalStore.proposal.is_quoted) {
                targetRoute = FormRouteName(ROUTE_NAMES.AboutYou, FORM_MODE.CREATE);
            }
            //If attempting to access the quote or payment page with a purchased quote, redirect to the thank you page
            if (proposalStore.proposal.is_purchased) {
                targetRoute = ROUTE_NAMES.ThankYou;
            }
        }

        // If attempting to access the quote preferences page without a UUID, redirect to the login page
        if (isPreferencesPage && !hasUUID) {
            targetRoute = ROUTE_NAMES.MarketingPreferencesLogin;
        }

        //If attempting to access the thank you page without a purchased quote, redirect to the quotes page
        if (isThankYouPage) {
            const previousRoute = router.options.history.state.back?.toString();
            if (previousRoute && previousRoute.split("?")[0] === ROUTE_PATHS.PaymentFailed) {
                targetRoute = ROUTE_NAMES.Payment;
            } else if (!proposalStore.proposal.is_purchased) {
                if (proposalStore.proposal.is_quoted) {
                    targetRoute = ROUTE_NAMES.PaymentFailed;
                    replaceRoute = true;
                } else {
                    targetRoute = FormRouteName(ROUTE_NAMES.AboutYou, FORM_MODE.CREATE);
                }
            }
        }
        if (targetRoute && targetRoute !== router.currentRoute.value.name) {
            const endRoute: RouteLocationRaw = {
                name: targetRoute
            };
            replaceRoute ? router.replace(endRoute) : router.push(endRoute);
        }
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export { routeGuard, resolveNavigation };

export default router;
